// ============================================================================
// SHORTAGE BOARD SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { shortageEndpoints } from '../endpoints/shortage.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, ID } from '../types';
import type {
  ExecuteShortagePlanDetailRequest,
  ShortageBoardQuery,
  ShortageNight,
  ShortagePlan,
  ShortagePlanDetail,
  UpsertShortagePlanDetailRequest,
  UpsertShortagePlanRequest,
} from '../types/shortage.types';

class ShortageService {
  /**
   * The board. Not paginated: the result is bounded by the number of nights that
   * are actually short in the window, which on a healthy property is zero and on
   * a bad week is a handful — and a manager needs to see all of them at once to
   * decide anything.
   */
  async getBoard(query: ShortageBoardQuery): Promise<ShortageNight[]> {
    const response = await get<ApiResponse<ShortageNight[]>>(
      buildEndpointWithQuery('/ShortageBoard', query as unknown as Record<string, unknown>),
    );
    return response.data?.data ?? [];
  }

  /** Opens the plan for a category + span; returns the existing one when there is one. */
  async getOrCreatePlan(request: UpsertShortagePlanRequest): Promise<ShortagePlan> {
    const response = await post<ApiResponse<ShortagePlan>>(shortageEndpoints.plans(), request);
    if (!response.data?.data) throw new Error(response.data?.message || 'Could not open the shortage plan.');
    return response.data.data;
  }

  async getPlan(id: ID): Promise<ShortagePlan | null> {
    const response = await get<ApiResponse<ShortagePlan>>(shortageEndpoints.plan(id));
    return response.data?.data ?? null;
  }

  async updatePlan(id: ID, request: UpsertShortagePlanRequest, rowVersion: number): Promise<ShortagePlan> {
    const response = await put<ApiResponse<ShortagePlan>>(
      `${shortageEndpoints.plan(id)}?rowVersion=${rowVersion}`,
      request,
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Could not update the shortage plan.');
    return response.data.data;
  }

  /** Records the decision for one booking on one night. Re-proposing clears any outcome. */
  async upsertDetail(request: UpsertShortagePlanDetailRequest): Promise<ShortagePlanDetail> {
    const response = await post<ApiResponse<ShortagePlanDetail>>(shortageEndpoints.planDetails(), request);
    if (!response.data?.data) throw new Error(response.data?.message || 'Could not save the plan line.');
    return response.data.data;
  }

  /**
   * Stamps what was actually done. This does NOT resolve the shortage — the real
   * operation runs through its own flow, and the board recomputes from inventory
   * on the next load. A line marked executed whose operation moved nothing leaves
   * the night just as short, correctly.
   */
  async executeDetail(id: ID, request: ExecuteShortagePlanDetailRequest): Promise<ShortagePlanDetail> {
    const response = await post<ApiResponse<ShortagePlanDetail>>(
      shortageEndpoints.executeDetail(id),
      request,
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Could not mark the plan line executed.');
    return response.data.data;
  }

  async abandonDetail(id: ID, rowVersion: number): Promise<ShortagePlanDetail> {
    const response = await post<ApiResponse<ShortagePlanDetail>>(
      `${shortageEndpoints.abandonDetail(id)}?rowVersion=${rowVersion}`,
      {},
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Could not abandon the plan line.');
    return response.data.data;
  }

  async deleteDetail(id: ID, rowVersion: number): Promise<boolean> {
    const response = await del<ApiResponse<boolean>>(
      `${shortageEndpoints.planDetail(id)}?rowVersion=${rowVersion}`,
    );
    return response.data?.data ?? false;
  }
}

export const shortageService = new ShortageService();
export default shortageService;
